import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { logout } from '~/auth/slices/auth.slice'
import { RootState } from '~/redux/store'

import { BackendError, SettingsErrors, SettingsFields, SettingsState, Tab } from '../types'

const INIT_SETTINGS_FIELDS = {
  email: '',
  nickname: '',
  country: '',
  curriculum: '',
  birthYear: '',
  wantsNewsletter: false,

  // These fields could be null.
  userType: '',
  skill: '',
  yearsInSchool: '',
  gradeLevelId: '',

  currPassword: '',
  newPassword: '',
  newPasswordConfirm: '',
  phone: '',
}

const initialState: SettingsState = {
  errors: {},
  fields: INIT_SETTINGS_FIELDS,
  serverError: null,
  isServerErrorInNicknameTab: null,
  tab: null,
}

const slice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setErrors: (state, action: PayloadAction<SettingsErrors>) => ({
      ...state,
      errors: action.payload,
    }),

    setFields: (state, action: PayloadAction<SettingsFields>) => ({
      ...state,
      fields: action.payload,
    }),

    setFieldValue: (state, action: PayloadAction<{ fieldName: string; value: number | string | boolean }>) => ({
      ...state,
      fields: {
        ...state.fields,
        [action.payload.fieldName]: action.payload.value,
      },
    }),

    setServerError: (state, action: PayloadAction<BackendError | null>) => ({
      ...state,
      serverError: action.payload,
    }),

    setIsServerErrorInNicknameTab: (state, action: PayloadAction<boolean | null>) => ({
      ...state,
      isServerErrorInNicknameTab: action.payload,
    }),

    setTab: (state, action: PayloadAction<Tab | null>) => ({
      ...state,
      tab: action.payload,
    }),
    resetPasswordFields: (state) => ({
      ...state,
      fields: { ...state.fields, newPassword: '', newPasswordConfirm: '', currPassword: '' },
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState)
  },
})

export const selectErrors = (state: RootState): SettingsErrors => state.settings.errors

export const selectFields = (state: RootState): SettingsFields => state.settings.fields

export const selectServerError = (state: RootState): BackendError | null => state.settings.serverError

export const selectIsServerErrorInNicknameTab = (state: RootState): boolean | null =>
  state.settings.isServerErrorInNicknameTab

export const selectTab = (state: RootState): Tab | null => state.settings.tab

export const {
  setErrors: setErrorsActionCreator,
  setFields: setFieldsActionCreator,
  setFieldValue: setFieldValueActionCreator,
  setServerError: setServerErrorActionCreator,
  setIsServerErrorInNicknameTab: setIsServerErrorInNicknameTabActionCreator,
  setTab: setTabActionCreator,
  resetPasswordFields: resetPasswordFieldActionCreator,
} = slice.actions

export default slice.reducer
